import React, { useState } from "react";

const Upload = () => {
  const [file, setFile] = useState(null);
  const [before, setBefore] = useState(null);
  const [after, setAfter] = useState(null);
  const [loading, setLoading] = useState(false);

  const waitForCv = () =>
    new Promise((resolve) => {
      const check = () => {
        if (window.cv) resolve();
        else setTimeout(check, 100);
      };
      check();
    });

  const orderPoints = (pts) => {
    const p = [
      { x: pts[0], y: pts[1] },
      { x: pts[2], y: pts[3] },
      { x: pts[4], y: pts[5] },
      { x: pts[6], y: pts[7] },
    ];

    const sum = p.map(pt => pt.x + pt.y);
    const diff = p.map(pt => pt.x - pt.y);

    return [
      p[sum.indexOf(Math.min(...sum))],
      p[diff.indexOf(Math.max(...diff))],
      p[sum.indexOf(Math.max(...sum))],
      p[diff.indexOf(Math.min(...diff))],
    ];
  };

  const scanDocument = async (imgEl) => {
    await waitForCv();
    const cv = window.cv;

    let src = cv.imread(imgEl);
    let gray = new cv.Mat();
    let blur = new cv.Mat();
    let edge = new cv.Mat();

    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
    cv.GaussianBlur(gray, blur, new cv.Size(5, 5), 0);
    cv.Canny(blur, edge, 75, 200);

    let contours = new cv.MatVector();
    let hierarchy = new cv.Mat();
    cv.findContours(edge, contours, hierarchy, cv.RETR_LIST, cv.CHAIN_APPROX_SIMPLE);

    let best = null;
    let maxArea = 0;

    for (let i = 0; i < contours.size(); i++) {
      let cnt = contours.get(i);
      let peri = cv.arcLength(cnt, true);
      let approx = new cv.Mat();
      cv.approxPolyDP(cnt, approx, 0.02 * peri, true);

      if (approx.rows === 4) {
        let area = cv.contourArea(approx);
        if (area > maxArea) {
          maxArea = area;
          best = approx;
        }
      }
    }

    if (!best) {
      alert("Could not detect document edges");
      return null;
    }

    const pts = orderPoints(best.data32F);

    const w1 = Math.hypot(pts[2].x - pts[3].x, pts[2].y - pts[3].y);
    const w2 = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
    const h1 = Math.hypot(pts[1].x - pts[2].x, pts[1].y - pts[2].y);
    const h2 = Math.hypot(pts[0].x - pts[3].x, pts[0].y - pts[3].y);

    const width = Math.max(w1, w2);
    const height = Math.max(h1, h2);

    const srcPts = cv.matFromArray(4, 1, cv.CV_32FC2, [
      pts[0].x, pts[0].y,
      pts[1].x, pts[1].y,
      pts[2].x, pts[2].y,
      pts[3].x, pts[3].y,
    ]);

    const dstPts = cv.matFromArray(4, 1, cv.CV_32FC2, [
      0, 0,
      width, 0,
      width, height,
      0, height,
    ]);

    let M = cv.getPerspectiveTransform(srcPts, dstPts);
    let warped = new cv.Mat();
    cv.warpPerspective(src, warped, M, new cv.Size(width, height));

    let canvas = document.createElement("canvas");
    cv.imshow(canvas, warped);

    src.delete(); gray.delete(); blur.delete(); edge.delete();
    contours.delete(); hierarchy.delete(); warped.delete();

    return canvas.toDataURL("image/jpeg", 0.95);
  };

  const handleUpload = async () => {
    if (!file) return alert("Select image");

    setLoading(true);
    const reader = new FileReader();

    reader.onload = async () => {
      setBefore(reader.result);

      const img = new Image();
      img.src = reader.result;
      img.onload = async () => {
        const scanned = await scanDocument(img);
        if (!scanned) return setLoading(false);

        setAfter(scanned);

        const docs = JSON.parse(localStorage.getItem("documents")) || [];
        docs.push({
          before: reader.result,
          after: scanned,
        });
        localStorage.setItem("documents", JSON.stringify(docs));
        setLoading(false);
      };
    };

    reader.readAsDataURL(file);
  };

  return (
    <div style={styles.fullScreen}>
      <div style={styles.container}>
        <h2 style={styles.heading}>📄 Scan Document</h2>

        <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} />
        <br /><br />

        <button style={styles.scanBtn} onClick={handleUpload}>
          {loading ? "Scanning..." : "Scan"}
        </button>

        <br /><br />

        <button style={styles.backBtn} onClick={() => window.location.href = "/dashboard"}>
          Back to Dashboard
        </button>

        {before && after && (
          <div style={styles.imageRow}>
            {[{ title: "Before", img: before }, { title: "After", img: after }].map((o, i) => (
              <div key={i} style={styles.imageBox}>
                <h4>{o.title}</h4>
                <img src={o.img} alt="" style={styles.image} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Upload;

const styles = {
  fullScreen: {
    background: "linear-gradient(to right, #0f2027, #203a43, #2c5364)",
    width: "100vw",
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  container: {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    padding: "40px",
    borderRadius: "20px",
    width: "90%",
    maxWidth: "900px",
    boxShadow: "0 0 20px rgba(255, 255, 255, 0.2)",
    textAlign: "center",
  },
  heading: {
    color: "#FFD700",
    fontSize: "36px",
  },
  scanBtn: {
    padding: "12px 30px",
    borderRadius: 12,
    border: "none",
    background: "#00C9A7",
    color: "#fff",
    fontWeight: "bold",
    cursor: "pointer",
  },
  backBtn: {
    padding: "12px 30px",
    borderRadius: 12,
    border: "none",
    background: "#FF7A00",
    color: "#fff",
    fontWeight: "bold",
    cursor: "pointer",
  },
  imageRow: {
    display: "flex",
    gap: 20,
    marginTop: 30,
    justifyContent: "center",
  },
  imageBox: {
    flex: 1,
    maxWidth: 420,
  },
  image: {
    width: "100%",
    borderRadius: 12,
  },
};
